import React, { useMemo, useState, Children } from 'react';
import { motion } from 'framer-motion';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
function formatEuro(value: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}
interface SliderInputProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
  prefix?: string;
  suffix?: string;
}
function SliderInput({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  prefix = '',
  suffix = ''
}: SliderInputProps) {
  return <div className="flex flex-col gap-2" data-id="element-3">
      <div className="flex items-center justify-between" data-id="element-4">
        <label className="text-sm font-medium text-slate-600" data-id="element-5">{label}</label>
        <div className="flex items-center gap-1" data-id="element-6">
          {prefix && <span className="text-sm text-slate-400" data-id="element-7">{prefix}</span>}
          <input type="number" value={value} onChange={e => {
          const v = Number(e.target.value);
          if (!isNaN(v)) onChange(Math.max(min, Math.min(max, v)));
        }} className="w-20 text-right text-sm font-semibold text-slate-900 bg-white border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#6C2BD9]/30 focus:border-[#6C2BD9]" min={min} max={max} step={step} data-id="element-8" />
          {suffix && <span className="text-sm text-slate-400" data-id="element-9">{suffix}</span>}
        </div>
      </div>
      <input type="range" value={value} onChange={e => onChange(Number(e.target.value))} min={min} max={max} step={step} className="w-full h-1.5 bg-slate-200 rounded-full appearance-none cursor-pointer accent-[#6C2BD9] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#6C2BD9] [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#6C2BD9] [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer" data-id="element-10" />
    </div>;
}
export function CostCalculator() {
  const [products, setProducts] = useState(0);
  const [campaignsPerProduct, setCampaignsPerProduct] = useState(0);
  const [countries, setCountries] = useState(0);
  const [masterSnakCost, setMasterSnakCost] = useState(2500);
  const [countryDeploymentCost, setCountryDeploymentCost] = useState(5);
  const [monthlyInsightsFee, setMonthlyInsightsFee] = useState(150);
  const [campaignDuration, setCampaignDuration] = useState(4);
  const calc = useMemo(() => {
    const totalCampaigns = products * campaignsPerProduct;
    const deploymentPerCampaign = countryDeploymentCost * countries;
    const insightsPerCampaign = monthlyInsightsFee * campaignDuration * countries;
    const costPerCampaign = masterSnakCost + deploymentPerCampaign + insightsPerCampaign;
    const totalAnnualCost = totalCampaigns * costPerCampaign;
    const annualMasterSnakCost = totalCampaigns * masterSnakCost;
    const annualDeploymentCost = totalCampaigns * deploymentPerCampaign;
    const annualInsightsCost = totalCampaigns * insightsPerCampaign;
    const totalDeployments = totalCampaigns * countries;
    const costPerDeployment = totalDeployments > 0 ? totalAnnualCost / totalDeployments : 0;
    return {
      totalCampaigns,
      totalDeployments,
      costPerCampaign,
      costPerDeployment,
      totalAnnualCost,
      annualMasterSnakCost,
      annualDeploymentCost,
      annualInsightsCost
    };
  }, [products, campaignsPerProduct, countries, masterSnakCost, countryDeploymentCost, monthlyInsightsFee, campaignDuration]);
  return <motion.div variants={containerVariants} initial="hidden" animate="show" className="w-full h-full overflow-y-auto" data-id="element-11">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-8 md:py-12" data-id="element-12">
        {/* Header */}
        <motion.div variants={itemVariants} className="mb-8" data-id="element-13">
          <div className="flex items-center gap-3 mb-2" data-id="element-14">
            <span className="text-2xl font-bold tracking-tight text-[#6C2BD9]" data-id="element-15">
              Snakker
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight" data-id="element-16">
            Fast Marketing<sup className="text-[0.6em] font-medium" data-id="element-17">™</sup>{' '}
            Cost Calculator
          </h1>
          <p className="text-lg text-slate-500 mt-2" data-id="element-18">
            Estimate your annual campaign operating cost
          </p>
          <p className="text-sm text-slate-400 mt-1" data-id="element-19">
            Adjust the variables to see how your portfolio scales across brands,
            campaigns, countries, and insights.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12" data-id="element-20">
          {/* Inputs */}
          <motion.div variants={itemVariants} className="space-y-6" data-id="element-21">
            <div className="bg-[#6C2BD9]/[0.07] rounded-2xl p-6 space-y-6 border-2 border-[#6C2BD9]/20" data-id="element-22">
              <div className="mb-1" data-id="element-23">
                <h3 className="text-sm font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-24">
                  Unit Economics
                </h3>
                <p className="text-xs text-[#6C2BD9]/60 mt-1" data-id="element-25">
                  Set your cost baseline first — everything else scales from
                  here.
                </p>
              </div>
              <SliderInput label="Master Snak Cost / Campaign" value={masterSnakCost} onChange={setMasterSnakCost} min={0} max={10000} step={100} prefix="€" data-id="element-26" />
              <SliderInput label="Country Deployment & Localization / Campaign" value={countryDeploymentCost} onChange={setCountryDeploymentCost} min={0} max={5000} step={100} prefix="€" data-id="element-27" />
              <SliderInput label="Monthly Insights Fee / Country" value={monthlyInsightsFee} onChange={setMonthlyInsightsFee} min={0} max={500} step={10} prefix="€" data-id="element-28" />
              <SliderInput label="Campaign Duration" value={campaignDuration} onChange={setCampaignDuration} min={0} max={12} suffix="months" data-id="element-29" />
            </div>

            <div className="bg-slate-50 rounded-2xl p-6 space-y-6 border border-slate-100" data-id="element-30">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2" data-id="element-31">
                Portfolio Scale
              </h3>
              <SliderInput label="Number of Brands" value={products} onChange={setProducts} min={0} max={50} data-id="element-32" />
              <SliderInput label="Campaigns per Brand / Year" value={campaignsPerProduct} onChange={setCampaignsPerProduct} min={0} max={6} data-id="element-33" />
              <SliderInput label="Number of Countries" value={countries} onChange={setCountries} min={0} max={50} data-id="element-34" />
            </div>
          </motion.div>

          {/* Output */}
          <motion.div variants={itemVariants} className="flex flex-col gap-6" data-id="element-35">
            {/* Main number */}
            <div className="bg-white rounded-2xl p-8 border-2 border-[#6C2BD9]/20 shadow-[0_0_40px_rgba(108,43,217,0.06)]" data-id="element-36">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3" data-id="element-37">
                Estimated Annual Cost
              </p>
              <p className="text-4xl md:text-5xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-38">
                {formatEuro(calc.totalAnnualCost)}
              </p>
              <p className="text-sm text-slate-400 mt-2" data-id="element-39">per year</p>
            </div>

            {/* Volume highlight */}
            <div className="bg-[#6C2BD9]/[0.04] rounded-2xl p-6 border border-[#6C2BD9]/10" data-id="element-40">
              <p className="text-xs font-semibold text-[#6C2BD9] uppercase tracking-widest mb-4" data-id="element-41">
                Campaign Volume
              </p>
              <div className="grid grid-cols-2 gap-4" data-id="element-42">
                <div data-id="element-43">
                  <p className="text-3xl font-bold text-slate-900" data-id="element-44">
                    {calc.totalCampaigns}
                  </p>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-45">
                    master campaigns / year
                  </p>
                </div>
                <div data-id="element-46">
                  <p className="text-3xl font-bold text-[#6C2BD9]" data-id="element-47">
                    {calc.totalDeployments}
                  </p>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-48">
                    localized deployments / year
                  </p>
                </div>
              </div>
              {calc.totalDeployments > 0 && <div className="mt-4 pt-4 border-t border-[#6C2BD9]/10 flex items-baseline gap-2" data-id="element-49">
                  <p className="text-2xl font-bold text-[#6C2BD9]" data-id="element-50">
                    {formatEuro(calc.costPerDeployment)}
                  </p>
                  <p className="text-sm text-slate-500" data-id="element-51">
                    per localized campaign
                  </p>
                </div>}
            </div>

            {/* Breakdown */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100" data-id="element-52">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-4" data-id="element-53">
                Cost Breakdown
              </p>
              <div className="space-y-3" data-id="element-54">
                <div className="flex justify-between items-center" data-id="element-55">
                  <span className="text-sm text-slate-600" data-id="element-56">
                    Cost per created and executed campaign
                  </span>
                  <span className="text-sm font-semibold text-slate-900" data-id="element-57">
                    {formatEuro(calc.costPerCampaign)}
                  </span>
                </div>
                <div className="h-px bg-slate-200 my-1" data-id="element-58" />
                <div className="flex justify-between items-center" data-id="element-59">
                  <span className="text-sm text-slate-500" data-id="element-60">
                    Annual Master Snak cost
                  </span>
                  <span className="text-sm text-slate-700" data-id="element-61">
                    {formatEuro(calc.annualMasterSnakCost)}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-62">
                  <span className="text-sm text-slate-500" data-id="element-63">
                    Annual deployment & localization
                  </span>
                  <span className="text-sm text-slate-700" data-id="element-64">
                    {formatEuro(calc.annualDeploymentCost)}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-65">
                  <span className="text-sm text-slate-500" data-id="element-66">
                    Annual insights layer
                  </span>
                  <span className="text-sm text-slate-700" data-id="element-67">
                    {formatEuro(calc.annualInsightsCost)}
                  </span>
                </div>
              </div>
            </div>

            {/* Strategic callout */}
            <div className="bg-[#6C2BD9]/5 rounded-2xl p-5 border border-[#6C2BD9]/10" data-id="element-68">
              <p className="text-sm font-medium text-[#6C2BD9] leading-relaxed" data-id="element-69">
                This is not software pricing. This is the annual cost of a fully
                managed campaign operating model across your portfolio.
              </p>
            </div>

            {/* Secondary message */}
            <p className="text-xs text-slate-400 leading-relaxed px-1" data-id="element-70">
              As you scale across more products, campaigns, or countries, cost
              grows predictably — while execution stays centralized.
            </p>
          </motion.div>
        </div>
      </div>
    </motion.div>;
}